import type { BoardMove, BoardTableState } from "../gen/bazivan/v1/table_pb";

/**
 * نمای سرورِ میزهای تخته‌ای → مدلِ محلیِ صفحه — همتای MenchOnlineController و
 * DoozOnlineController ِ اپ. خالص و بی DOM، تا آزمون مستقیم بسنجدش.
 *
 * هیچ قاعده‌ای این‌جا نیست: حرکتِ مجاز را سرور در `legal` می‌گوید. این‌جا فقط
 * چرخشِ صندلی‌ها (من = ۰، پایینِ صفحه) و هندسه‌ی تخته است.
 *
 * رمزِ نما (Backend/internal/boards):
 *   منچ  — board[صندلی·۴ + مهره] = جای مهره (−۱ خانه، ۰..۵۰ حلقه، ۵۱..۵۶ راهروِ پایانی؛
 *          مهره‌ی بیرون‌از‌بازیِ «سریع» هم ۵۶ می‌آید) · extra = [گوشه‌ی هر صندلی…] ++ دُمِ
 *          نسخه‌ی ۲ (menchTail) · dice = [تاسِ آخر] · phase ۰ تاس، ۱ انتخابِ مهره
 *          · legal: {a مهره، b مقصد، c پرچم‌ها} — b و c فقط با دُمِ نسخه‌ی ۲ معنا دارند
 *   دوز  — board[ستون·۶ + ردیف] = صندلی یا −۱ · extra = خانه‌های چهارتای برنده
 *          · winner −۱ و round ۱ = مساوی
 */

export const BASE = -1, MAIN_LAST = 50, HOME0 = 51, GOAL = 56, LOOP = 52;

/** صندلیِ سرور → صندلیِ محلی (من = ۰). */
export const toLocal = (server: number, me: number, n: number): number =>
  n <= 0 ? server : (((server - me) % n) + n) % n;
/** صندلیِ محلی → صندلیِ سرور. */
export const toServer = (local: number, me: number, n: number): number =>
  n <= 0 ? local : (((local + me) % n) + n) % n;

/** piece: آدمکِ منچ — ۰ پسر، ۱ دختر (انتخابِ خودِ بازیکن؛ ربات از چهره‌اش). */
export type BoardSeat = { name: string; avatar: string; bot: boolean; connected: boolean; playerId: string; piece: number;
  /** تاسِ خریده‌ی این بازیکن (Seat.dice)؛ خالی = عاج — Die3D با همین می‌کشد (diceSkins.ts). */
  dice?: string;
  /** کشور (Seat.country، از IP) برای نقشه‌ی کنارِ آواتار؛ خالی = پنهان یا نامعلوم. */
  country?: string };

type Common = {
  game: string;
  tableId: string;
  n: number;
  /** صندلیِ من روی سرور (تماشاچی: ۰). */
  me: number;
  watching: boolean;
  /** نوبت، به صندلیِ محلی. */
  turn: number;
  /** به ترتیبِ صندلیِ محلی. */
  seats: BoardSeat[];
  over: boolean;
  /** برنده به صندلیِ محلی؛ −۱ = هنوز، یا مساوی. */
  winner: number;
  /** نوبتِ من است و سرور حرکتی از من می‌خواهد (جز تماشاچی). */
  mine: boolean;
  legal: BoardMove[];
};

/**
 * قانون‌های منچِ نسخه‌ی ۲ — همان mench.Options ِ سرور و MenchOptions ِ اپ. نرمال‌شده
 * می‌رسند (تیمی فقط با چهار نفر، «تا نفرِ آخر» هرگز با تیمی)؛ این‌جا دوباره نرمال نمی‌شوند.
 */
export type MenchOpts = {
  start: number; safe: boolean; pair: boolean; bonus: boolean; teams: boolean; fast: boolean; places: boolean;
  /** نسخه‌ی ۳: شکارچی، شروعِ سریع، نیروها، و زمان (۰ بی‌زمان، ۱ پنج دقیقه، ۲ ده دقیقه). */
  hunter: boolean; quick: boolean; power: boolean; timed: number;
};

/** یک حرکتِ مجاز با مقصد و پرچم‌هایش (legal.b و legal.c ِ سرور). */
export type MenchHint = { tok: number; target: number; flags: number };

export type MenchLocal = Omit<Common, "game"> & {
  game: "mench";
  /** گوشه‌ی هر صندلیِ محلی — چرخیده، گوشه‌ی من ۰ (پایین-چپ). */
  corner: number[];
  /** گوشه‌ی من روی سرور: رنگِ گوشه‌ی محلیِ c = COLORS[(c + shift) % 4]. */
  shift: number;
  /** جای مهره‌ها؛ مهره‌ی بیرون‌از‌بازیِ «سریع» = MENCH_NONE (−۲). */
  pos: number[][];
  die: number;
  phase: number;
  /**
   * مهره‌های مجاز (فاز ۱، نوبتِ من) — مالِ صندلیِ `owner`، همان فهرستِ سرور بی
   * یکی‌کردنِ دوباره: سرور از مهره‌های هم‌جا (همه‌ی خانه‌نشین‌ها، دو مهره‌ی دیوار) فقط
   * کوچک‌ترین را می‌فرستد و لمسِ هر کدام همان را می‌فرستد (menchPickable).
   */
  tokens: number[];
  canRoll: boolean;
  /** دُمِ نسخه‌ی ۲ رسید؟ نه = سرورِ قدیمی: همه‌چیز کلاسیک، بی راهنما. */
  v2: boolean;
  /** تمِ تخته (۰ کلاسیک، ۱ کاشی، ۲ فرش، ۳ باغِ شب) — سرور هر مسابقه قرعه می‌کشد. */
  theme: number;
  mask: number;
  opt: MenchOpts;
  /** شانس‌های مانده‌ی همین تاس («سه شانس»)؛ بیشتر از ۱ = خال‌های زیرِ تاس. */
  tries: number;
  /** رتبه‌ی هر صندلیِ محلی — ۰ هنوز بازی می‌کند (تیمی: هر دو یار یک رتبه). */
  rank: number[];
  /** آمار به صندلیِ محلی: زد، زده شد، شش. */
  hits: number[];
  lost: number[];
  sixes: number[];
  /**
   * صاحبِ مهره‌هایی که این نوبت جابه‌جا می‌شوند (صندلیِ محلی). جز در تیمی همان
   * turn است؛ در تیمی، کسی که مهره‌هایش رسیده برای یارش تاس می‌ریزد.
   */
  owner: number;
  /** راهنمای حرکت‌های مجاز (فقط نوبتِ من در فاز ۱ و فقط با دُمِ نسخه‌ی ۲). */
  hints: MenchHint[];
  /** نسخه‌ی ۳ — «شکارچی»: دروازه‌ی راهروِ هر صندلیِ محلی باز است (بی قانون همه باز). */
  gate: boolean[];
  /** «زمان‌دار»: میلی‌ثانیه‌ی مانده هنگامِ رسیدنِ نما و زمانِ رسیدن (performance.now) — ۰ = بی‌زمان. */
  leftMs: number;
  leftAt: number;
  /** ضریبِ همین تاس (۲ = «دوبرابر»). */
  mult: number;
  /** «نیروها»: نیروی چهار خانه‌ی درخشان (MENCH_POWER_CELLS)، ۰ خالی، ۱ سپر، ۲ دوبرابر، ۳ جابه‌جایی. */
  cells: number[];
  /** نیروی دستِ هر صندلیِ محلی. */
  shield: boolean[];
  dbl: boolean[];
};

export type DoozLocal = Omit<Common, "game"> & {
  game: "dooz";
  /** cells[ستون][ردیف] = صندلیِ محلی یا −۱؛ ردیفِ ۰ پایین. */
  cells: number[][];
  heights: number[];
  /** رنگِ صندلیِ ۰ ِ محلی: رنگ = (صندلی + shift) mod ۲ — ۰ قرمز، ۱ طلایی. */
  shift: number;
  draw: boolean;
  line: [number, number][];
  /** ستون‌های مجازِ من. */
  cols: number[];
};

/** تخته‌نرد، از چشمِ من: همیشه «من» صندلیِ ۰ است که از ۲۳ به ۰ می‌رود و خانه‌اش ۰..۵ است. */
export type NardLocal = Omit<Common, "game"> & {
  game: "nard";
  /** تعدادِ علامت‌دار: + مالِ من (صندلیِ ۰ ِ محلی)، − مالِ حریف. */
  pts: number[];
  bar: [number, number];
  off: [number, number];
  /** تاس‌های مانده‌ی این نوبت. */
  dice: number[];
  rolled: [number, number];
  phase: number;
  opening: boolean;
  /** حرکت‌های مجازِ من (از، به، تاس) به مختصاتِ محلی؛ −۱ بار، −۲ جمع. */
  moves: { from: number; to: number; die: number }[];
  canRoll: boolean;
  /** رنگ: صندلیِ محلیِ p عاج است اگر (p + shift) زوج باشد. */
  shift: number;
  points: number;
};

export type DominoLocal = Omit<Common, "game"> & {
  game: "domino";
  /** زنجیره از چپ به راست، همان‌طور که دیده می‌شود. */
  chain: [number, number][];
  /** شماره‌ی مهره‌ی اول (مرکز) در `chain` — زنجیره از آن دو سو مار می‌شود (dominoGeom). */
  center: number;
  hand: [number, number][];
  counts: number[];
  yard: number;
  leftEnd: number;
  rightEnd: number;
  /** گذاشتن‌های مجازِ من: شماره‌ی مهره و سر (−۱ اول، ۰ چپ، ۱ راست). */
  plays: { index: number; side: number }[];
  canDraw: boolean;
  roundOver: boolean;
  roundWinner: number;
  roundPoints: number;
  blocked: boolean;
  round: number;
  score: number[];
  shift: number;
};

/** خانه‌ی جادوییِ مارپله. نوع: ۱ پادزهر، ۲ شمشیر، ۳ تاسِ دوباره، ۴ جابه‌جایی با نفرِ اول، ۵ ایست، ۶ ماشین. */
export type MarpeleMagic = { cell: number; kind: number };
export const MAGIC_ANTIDOTE = 1, MAGIC_SWORD = 2, MAGIC_EXTRA = 3, MAGIC_SWAP = 4, MAGIC_SKIP = 5, MAGIC_CAR = 6;
/** ماشین (کاربر، ۲۶ اکتبر ۲۰۲۶): نوبت‌هایی که بعد از نوبتِ سوارشدن می‌ماند — همان marpele.CarTurns. */
export const CAR_TURNS = 3;
/** نیروهای یک صندلی: پادزهر و شمشیرِ در دست، نوبتِ سوخته‌ی بعدی، و نوبت‌های ماندهٔ ماشین (۰ = پیاده). */
export interface MarpelePower { antidote: number; sword: number; skip: boolean; car: number }
/** تاس: ۰ یکی، ۱ دو تاس و یکی را انتخاب کن، ۲ جمعِ دو تاس (سریع، با برگشت از صد). */
export const DICE_ONE = 0, DICE_CHOOSE = 1, DICE_SUM = 2;

export type MarpeleLocal = Omit<Common, "game"> & {
  game: "marpele";
  pos: number[];
  die: number;
  /** تاس‌های همین نوبت (یک یا دو). */
  dice: number[];
  canRoll: boolean;
  /** نوبتِ من و دو تاس: کدام تاس‌ها را می‌شود برداشت (فاز ۱). */
  choose: number[];
  /** نوبتِ من و سرِ مار با نیرو در دست (فاز ۲): چه راه‌هایی هست. */
  bite: { head: number; antidote: boolean; sword: boolean } | null;
  /** سرِ مارِ منتظرِ تصمیم (هر صندلی؛ ۰ = هیچ) — برای نشان دادن به همه. */
  pendingHead: number;
  shift: number;
  /** چیدمانِ همین مسابقه (از extra ِ سرور؛ هر مسابقه تخته‌ی خودش را دارد). */
  jumps: Readonly<Record<number, number>>;
  magics: MarpeleMagic[];
  /** سرِ مارهایی که شمشیر خورده‌اند — دیگر نیش نمی‌زنند. */
  dead: number[];
  /** نیروهای هر صندلیِ محلی. */
  powers: MarpelePower[];
  theme: number;
  diceMode: number;
  magic: boolean;
};

export type BoardLocal = MenchLocal | DoozLocal | NardLocal | DominoLocal | MarpeleLocal;

function common(st: BoardTableState): Common {
  const n = st.seats.length;
  const watching = st.watching;
  const me = watching || n === 0 ? 0 : Math.min(Math.max(0, st.me), n - 1);
  const seats: BoardSeat[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const s = st.seats[i]!;
    seats[toLocal(i, me, n)] = { name: s.name, avatar: s.avatar, bot: s.bot, connected: s.connected, playerId: s.playerId, piece: s.piece === 1 ? 1 : 0, dice: s.dice ?? "", country: s.country ?? "" };
  }
  const turn = toLocal(st.turn, me, n);
  return {
    game: st.game, tableId: st.tableId, n, me, watching, turn, seats,
    over: st.over,
    winner: st.over && st.winner >= 0 ? toLocal(st.winner, me, n) : -1,
    mine: !watching && !st.over && turn === 0 && st.legal.length > 0,
    legal: st.legal,
  };
}

export const MENCH_CORNERS = (n: number): number[] => (n === 2 ? [0, 2] : n === 3 ? [0, 1, 2] : [0, 1, 2, 3]);

/** جای درونیِ مهره‌ی بیرون‌از‌بازی («سریع»: مهره‌های ۲ و ۳). روی سیم ۵۶ می‌آید. */
export const MENCH_NONE = -2;

/** پرچم‌های راهنمای حرکت (legal.c) — همان mench.go و MenchHint ِ اپ. */
export const HINT_CAPTURE = 1, HINT_SAFE = 2, HINT_DANGER = 4, HINT_GOAL = 8, HINT_LANE = 16, HINT_PAIR = 32;

/** خانه‌های امنِ حلقه (مطلق): چهار خانه‌ی شروع و ستاره‌ی هشت خانه بعدشان — با هر تعدادِ بازیکن همین هشت‌تا. */
export const MENCH_SAFE: readonly number[] = [0, 8, 13, 21, 26, 34, 39, 47];
export const menchSafeAbs = (abs: number): boolean => MENCH_SAFE.includes(((abs % LOOP) + LOOP) % LOOP);

/** نامِ تم‌ها — همان MenchArt ِ اپ و tools/mench_board_art.py (۰ چهارباغ، ۱ کاشیِ لاجوردی، ۲ فرش، ۳ چهارباغ در شب). */
export const MENCH_THEMES = ["چهارباغ", "کاشی", "فرش", "باغ شب"] as const;

export const MENCH_CLASSIC: MenchOpts = {
  start: 0, safe: false, pair: false, bonus: false, teams: false, fast: false, places: false,
  hunter: false, quick: false, power: false, timed: 0,
};

/** «نیروها»: چهار خانه‌ی درخشانِ حلقه (مطلق) و نیروهایشان — همان mench.PowerCells ِ سرور. */
export const MENCH_POWER_CELLS: readonly number[] = [4, 17, 30, 43];
export const POWER_SHIELD = 1, POWER_DOUBLE = 2, POWER_SWAP = 3;

/**
 * ماسک → قانون‌ها: start | safe<<2 | pair<<3 | bonus<<4 | teams<<5 | fast<<6 | places<<7 | hunter<<8 |
 * quick<<9 | power<<10 | timed<<11 (start و timed ِ بیرون از بازه = ۰).
 */
export function menchOpts(mask: number): MenchOpts {
  const start = mask & 3, timed = (mask >> 11) & 3;
  return {
    start: start > 2 ? 0 : start, safe: (mask & 4) !== 0, pair: (mask & 8) !== 0, bonus: (mask & 16) !== 0,
    teams: (mask & 32) !== 0, fast: (mask & 64) !== 0, places: (mask & 128) !== 0,
    hunter: (mask & 256) !== 0, quick: (mask & 512) !== 0, power: (mask & 1024) !== 0, timed: timed > 2 ? 0 : timed,
  };
}

export const menchMask = (o: MenchOpts): number =>
  o.start | (o.safe ? 4 : 0) | (o.pair ? 8 : 0) | (o.bonus ? 16 : 0) | (o.teams ? 32 : 0) | (o.fast ? 64 : 0) | (o.places ? 128 : 0) |
  (o.hunter ? 256 : 0) | (o.quick ? 512 : 0) | (o.power ? 1024 : 0) | ((o.timed & 3) << 11);

/** دُمِ نسخه‌ی ۲، هنوز به ترتیبِ صندلیِ سرور. */
export type MenchTail = {
  theme: number; mask: number; tries: number;
  rank: number[]; hits: number[]; lost: number[]; sixes: number[];
  /** صاحبِ مهره‌های نوبت — صندلیِ سرور. */
  owner: number;
  /** نسخه‌ی ۳ (اگر آمده): دروازه‌های باز (بیتِ صندلیِ سرور)، زمانِ مانده، ضریب، خانه‌های درخشان، نیروی دست. */
  v3: { gates: number; leftMs: number; mult: number; cells: number[]; held: number[] } | null;
};

/**
 * دُمِ نسخه‌ی ۲ ِ extra (mench.Fill): پس از n گوشه
 *   [۲، تم، ماسک، شانسِ مانده، n، رتبه‌ها×n، (زد، زده شد، شش)×n، صاحب].
 * فقط وقتی خوانده می‌شود که درازا بس باشد، نشانه ≥ ۲ باشد و n ِ درونش با n ِ میز بخواند؛
 * وگرنه null = سرورِ قدیمی و همه‌چیز کلاسیک. نشانه‌ی بزرگ‌تر و عددهای اضافه ته (نسخه‌های
 * بعد) نادیده می‌مانند — دُم فقط از ته دراز می‌شود.
 */
export function menchTail(extra: readonly number[], n: number): MenchTail | null {
  if (n <= 0 || extra.length < n + 6 + 4 * n || extra[n]! < 2 || extra[n + 4] !== n) return null;
  const at = (k: number) => extra[k] ?? 0;
  const rank: number[] = [], hits: number[] = [], lost: number[] = [], sixes: number[] = [];
  for (let s = 0; s < n; s++) rank.push(at(n + 5 + s));
  for (let s = 0; s < n; s++) {
    const k = 2 * n + 5 + 3 * s;
    hits.push(at(k)); lost.push(at(k + 1)); sixes.push(at(k + 2));
  }
  // نسخه‌ی ۳ پشتِ صاحب: [دروازه‌ها، میلی‌ثانیه‌ی مانده، ضریب، خانه‌های درخشان×۴، نیروی دستِ هر صندلی×n]
  const v = 5 * n + 6;
  const v3 = extra.length >= v + 7 + n
    ? { gates: at(v), leftMs: Math.max(0, at(v + 1)), mult: at(v + 2) === 2 ? 2 : 1,
        cells: [0, 1, 2, 3].map((i) => at(v + 3 + i)), held: Array.from({ length: n }, (_, s) => at(v + 7 + s)) }
    : null;
  return { theme: at(n + 1), mask: at(n + 2), tries: at(n + 3), rank, hits, lost, sixes, owner: at(5 * n + 5), v3 };
}

export function menchLocal(st: BoardTableState): MenchLocal {
  const c = common(st);
  const fallback = MENCH_CORNERS(c.n);
  const cornerOf = (server: number) => (server < st.extra.length ? st.extra[server]! : fallback[server] ?? 0);
  const shift = c.n > 0 ? cornerOf(c.me) : 0;
  const tail = menchTail(st.extra, c.n);
  const opt = tail ? menchOpts(tail.mask) : MENCH_CLASSIC;
  const corner: number[] = [], pos: number[][] = [];
  const rank: number[] = [], hits: number[] = [], lost: number[] = [], sixes: number[] = [];
  const gate: boolean[] = [], shield: boolean[] = [], dbl: boolean[] = [];
  const v3 = tail?.v3 ?? null;
  for (let l = 0; l < c.n; l++) {
    const sv = toServer(l, c.me, c.n);
    corner.push((((cornerOf(sv) - shift) % 4) + 4) % 4);
    const p: number[] = [];
    // «سریع»: مهره‌های ۲ و ۳ ِ همه بیرون از بازی‌اند، هر عددی که روی سیم آمده باشد (۵۶)
    for (let t = 0; t < 4; t++) p.push(opt.fast && t >= 2 ? MENCH_NONE : st.board[sv * 4 + t] ?? BASE);
    pos.push(p);
    // آرایه‌های صندلی‌دارِ دُم به ترتیبِ سرورند: صندلیِ محلیِ l = سرورِ (l + me) mod n
    rank.push(tail?.rank[sv] ?? 0); hits.push(tail?.hits[sv] ?? 0);
    lost.push(tail?.lost[sv] ?? 0); sixes.push(tail?.sixes[sv] ?? 0);
    gate.push(!v3 || ((v3.gates >> sv) & 1) === 1);
    shield.push(!!v3 && ((v3.held[sv] ?? 0) & 1) !== 0);
    dbl.push(!!v3 && ((v3.held[sv] ?? 0) & 2) !== 0);
  }
  const moves = c.mine && st.phase === 1 ? st.legal.filter((m) => m.kind === 2) : [];
  const tokens = [...new Set(moves.map((m) => m.a))];
  // راهنما فقط با دُم: سرورِ قدیمی b و c را صفر می‌فرستد و صفر یعنی «بی‌پرچم به خانه‌ی ۰» ِ دروغ
  const hints: MenchHint[] = [];
  if (tail) for (const m of moves) if (!hints.some((h) => h.tok === m.a)) hints.push({ tok: m.a, target: m.b, flags: m.c });
  const owner = tail && tail.owner >= 0 && tail.owner < c.n ? toLocal(tail.owner, c.me, c.n) : c.turn;
  return {
    ...c, game: "mench", corner, shift, pos,
    die: st.dice[0] ?? 0,
    phase: st.phase,
    tokens,
    canRoll: c.mine && st.phase === 0,
    v2: tail !== null, theme: tail ? ((tail.theme % 4) + 4) % 4 : 0, mask: tail ? menchMask(opt) : 0, opt,
    tries: tail ? tail.tries : 1, rank, hits, lost, sixes, owner, hints,
    gate, leftMs: v3?.leftMs ?? 0, leftAt: performance.now(), mult: v3?.mult ?? 1,
    cells: v3?.cells.map((k) => (k >= 0 && k <= 3 ? k : 0)) ?? [0, 0, 0, 0], shield, dbl,
  };
}

/**
 * لمسِ مهره‌ی t ِ صندلیِ s کدام مهره را می‌فرستد؟ −۱ = هیچ.
 *
 * قاعده‌ی §4.5: سرور از مهره‌های هم‌جای صاحبِ نوبت فقط کوچک‌ترین را می‌فرستد. پس اگر
 * مهره‌ی فهرست‌شده در خانه (یا روی دیوار) است، **هر** مهره‌ی همان‌جا روشن است و لمسش همان
 * مهره‌ی فهرست‌شده را می‌فرستد — بازیکن نباید بفهمد کدام «کوچک‌تر» است.
 */
export function menchPickable(m: MenchLocal, s: number, t: number): number {
  if (s !== m.owner || m.tokens.length === 0) return -1;
  if (m.tokens.includes(t)) return t;
  const p = m.pos[s]?.[t];
  if (p === undefined || p === MENCH_NONE || p === GOAL || p > MAIN_LAST) return -1;   // راهرو هم‌جا ندارد
  return m.tokens.find((x) => m.pos[s]![x] === p) ?? -1;
}

/** نشانِ روی شبحِ یک مقصد — فقط بالاترین: زدن > رسیدن > خطر > دیوار > امن. */
export type MenchBadge = "capture" | "goal" | "danger" | "pair" | "safe" | "";
const BADGE_RANK: Record<MenchBadge, number> = { capture: 5, goal: 4, danger: 3, pair: 2, safe: 1, "": 0 };
export const menchBadgeRank = (b: MenchBadge): number => BADGE_RANK[b];

/**
 * نشانِ یک راهنما. «امن» فقط وقتی که مقصد ستاره یا خانه‌ی شروع با «خانه‌ی امن» روشن است؛
 * پرچمِ ۲ برای راهرو، مقصد و دیوار هم روشن است ولی آن‌ها نشانِ خودشان را دارند یا نیازی ندارند.
 */
export function menchBadge(h: MenchHint, opt: MenchOpts, corner: number): MenchBadge {
  if ((h.flags & HINT_CAPTURE) !== 0) return "capture";
  if ((h.flags & HINT_GOAL) !== 0) return "goal";
  if ((h.flags & HINT_DANGER) !== 0) return "danger";
  if ((h.flags & HINT_PAIR) !== 0) return "pair";
  if ((h.flags & HINT_SAFE) !== 0 && opt.safe && h.target >= 0 && h.target <= MAIN_LAST && menchSafeAbs(corner * 13 + h.target)) return "safe";
  return "";
}

/**
 * برده‌ام؟ با دُم: رتبه‌ی خودم ۱ است (تیمی: رتبه‌ی تیم، هر دو یار ۱). بی دُم: winner همان من.
 * تماشاچی هیچ‌وقت نبرده.
 */
export function menchWon(m: MenchLocal): boolean {
  if (m.watching) return false;
  if (m.v2) return (m.rank[0] ?? 0) === 1;
  return m.over && m.winner === 0;
}

/** مهره‌های در بازیِ یک صندلی (سریع: دو). */
export const menchInPlay = (m: MenchLocal, s: number): number => m.pos[s]?.filter((p) => p !== MENCH_NONE).length ?? 0;

/** چند درصدِ راه را رفته — همان ترتیبِ رتبه‌بندیِ سرور: (خانه ۰، وگرنه جا + ۱) از ۵۷ برای هر مهره. */
export function menchProgress(m: MenchLocal, s: number): number {
  const ps = (m.pos[s] ?? []).filter((p) => p !== MENCH_NONE);
  if (ps.length === 0) return 0;
  const sum = ps.reduce((a, p) => a + (p === BASE ? 0 : Math.min(GOAL, p) + 1), 0);
  return Math.round((sum * 100) / (ps.length * (GOAL + 1)));
}

/** یارِ یک صندلیِ محلی در تیمی — روبه‌رو: (s + ۲) mod ۴ هم در سرور و هم پس از چرخش. */
export const menchPartner = (s: number): number => (s + 2) % 4;

export const DOOZ_W = 7, DOOZ_H = 6;

export function doozLocal(st: BoardTableState): DoozLocal {
  const c = common(st);
  const cells: number[][] = [], heights: number[] = [];
  for (let col = 0; col < DOOZ_W; col++) {
    const colCells: number[] = [];
    let h = 0;
    for (let row = 0; row < DOOZ_H; row++) {
      const v = st.board[col * DOOZ_H + row] ?? -1;
      colCells.push(v < 0 ? -1 : toLocal(v, c.me, c.n));
      if (v >= 0) h = row + 1;
    }
    cells.push(colCells);
    heights.push(h);
  }
  return {
    ...c, game: "dooz", cells, heights,
    shift: c.me & 1,
    draw: st.over && st.winner < 0,
    line: st.extra.map((x) => [Math.floor(x / DOOZ_H), x % DOOZ_H] as [number, number]),
    cols: c.mine ? [...new Set(st.legal.filter((m) => m.kind === 2).map((m) => m.a))] : [],
  };
}

// ─────────────────────────── هندسه‌ی منچ (همان MenchView.cs) ───────────────────────────

/** ۵۲ خانه‌ی حلقه (ستون، ردیف) به ترتیبِ حرکت؛ ردیفِ ۰ بالاست، پیش از چرخش. */
export const MENCH_LOOP: readonly [number, number][] = [
  [1, 6], [2, 6], [3, 6], [4, 6], [5, 6],
  [6, 5], [6, 4], [6, 3], [6, 2], [6, 1], [6, 0],
  [7, 0], [8, 0],
  [8, 1], [8, 2], [8, 3], [8, 4], [8, 5],
  [9, 6], [10, 6], [11, 6], [12, 6], [13, 6], [14, 6],
  [14, 7], [14, 8],
  [13, 8], [12, 8], [11, 8], [10, 8], [9, 8],
  [8, 9], [8, 10], [8, 11], [8, 12], [8, 13], [8, 14],
  [7, 14], [6, 14],
  [6, 13], [6, 12], [6, 11], [6, 10], [6, 9],
  [5, 8], [4, 8], [3, 8], [2, 8], [1, 8], [0, 8],
  [0, 7], [0, 6],
];

/** راهروِ پایانیِ هر گوشه (۶ خانه، آخری = مقصد). */
export const MENCH_HOME: readonly (readonly [number, number][])[] = [
  [[1, 7], [2, 7], [3, 7], [4, 7], [5, 7], [6, 7]],
  [[7, 1], [7, 2], [7, 3], [7, 4], [7, 5], [7, 6]],
  [[13, 7], [12, 7], [11, 7], [10, 7], [9, 7], [8, 7]],
  [[7, 13], [7, 12], [7, 11], [7, 10], [7, 9], [7, 8]],
];

/** جای چهار مهره‌ی بیرون‌نیامده در خانه‌ی هر گوشه. */
export const MENCH_YARD: readonly (readonly [number, number][])[] = [
  [[1.5, 1.5], [3.5, 1.5], [1.5, 3.5], [3.5, 3.5]],
  [[10.5, 1.5], [12.5, 1.5], [10.5, 3.5], [12.5, 3.5]],
  [[10.5, 10.5], [12.5, 10.5], [10.5, 12.5], [12.5, 12.5]],
  [[1.5, 10.5], [3.5, 10.5], [1.5, 12.5], [3.5, 12.5]],
];

/** رنگِ هر گوشه روی سرور — قرمز، سبز، زرد، آبی (همان منچِ خانگی و MenchView.CC). */
export const MENCH_COLORS = ["#d9433a", "#46a24b", "#e0b23a", "#3f7fd4"] as const;

export const menchColor = (localCorner: number, shift: number): string =>
  MENCH_COLORS[(((localCorner + shift) % 4) + 4) % 4]!;

/**
 * مرکزِ خانه‌ی (ستون، ردیف) روی صفحه، در واحدِ خانه (۰..۱۵، y رو به پایین) — پس از
 * چرخشِ ۹۰° پادساعت‌گرد تا گوشه‌ی ۰ (رنگِ من) پایین-چپ بنشیند: (c, r) → (r, ۱۴ − c).
 */
export const menchXY = (col: number, row: number): [number, number] => [row + 0.5, 14 - col + 0.5];

/** جای یک مهره روی صفحه (واحدِ خانه). */
export function menchTokenXY(corner: number, tok: number, pos: number): [number, number] {
  if (pos === BASE) { const y = MENCH_YARD[corner]![tok]!; return menchXY(y[0], y[1]); }
  if (pos <= MAIN_LAST) { const c = MENCH_LOOP[(corner * 13 + pos) % LOOP]!; return menchXY(c[0], c[1]); }
  const h = Math.min(5, Math.max(0, pos - HOME0));
  const c = MENCH_HOME[corner]![h]!;
  return menchXY(c[0], c[1]);
}

/** نامِ نیروها (۱ سپر، ۲ دوبرابر، ۳ جابه‌جایی). */
export const POWER_FA = ["", "سپر", "تاس دوبرابر", "جابه‌جایی"] as const;

/**
 * جای خانه‌ی درخشانِ i روی صفحه — خانه‌ها مطلق‌اند (سرور)، صفحه چرخیده تا گوشه‌ی من ۰ باشد:
 * خانه‌ی محلی = مطلق − ۱۳ × shift.
 */
export function menchPowerXY(i: number, shift: number): [number, number] {
  const local = ((((MENCH_POWER_CELLS[i] ?? 0) - 13 * shift) % LOOP) + LOOP) % LOOP;
  const c = MENCH_LOOP[local]!;
  return menchXY(c[0], c[1]);
}

/** خانه‌های مسیرِ یک حرکت برای انیمیشن — بیرون‌آمدن یک پرش است، بقیه خانه‌به‌خانه. */
export function menchPath(from: number, to: number): number[] {
  if (from === BASE) return [0];
  const out: number[] = [];
  for (let p = from + 1; p <= to; p++) out.push(p);
  return out;
}

// ─────────────────────────── رنگِ دوز ───────────────────────────

export const DOOZ_RED = "#d9433a", DOOZ_GOLD = "#e0b23a";
/** رنگِ مهره‌ی یک صندلیِ محلی — همان رنگِ سرور روی همه‌ی صفحه‌ها. */
export const doozColor = (seat: number, shift: number): string => (((seat + shift) & 1) === 0 ? DOOZ_RED : DOOZ_GOLD);

// ─────────────────────────── تخته‌نرد ───────────────────────────

export const NARD_BAR = -1, NARD_OFF = -2;

export function nardLocal(st: BoardTableState): NardLocal {
  const c = common(st);
  // صندلیِ ۱ ِ سرور: تخته آینه می‌شود (خانه‌ی i → ۲۳ − i، علامت‌ها وارونه) تا خانه‌ی من پایین-راست باشد
  const mirror = (c.me & 1) === 1;
  const map = (p: number) => (p < 0 || !mirror ? p : 23 - p);
  const pts: number[] = [];
  for (let i = 0; i < 24; i++) {
    const v = st.board[map(i)] ?? 0;
    pts.push(mirror ? -v : v);
  }
  const x = (k: number) => st.extra[k] ?? 0;
  const bar: [number, number] = mirror ? [x(1), x(0)] : [x(0), x(1)];
  const off: [number, number] = mirror ? [x(3), x(2)] : [x(2), x(3)];
  const moves = c.mine && st.phase === 1
    ? st.legal.filter((m) => m.kind === 2).map((m) => ({ from: map(m.a), to: map(m.b), die: m.c })) : [];
  return {
    ...c, game: "nard", pts, bar, off, dice: [...st.dice], rolled: [x(4), x(5)], phase: st.phase,
    opening: x(6) === 1, moves, canRoll: c.mine && st.phase === 0, shift: c.me & 1,
    points: st.over && st.winner >= 0 ? st.score[st.winner] ?? 0 : 0,
  };
}

/** نگاشتِ خانه‌ی محلی ↔ سرور (آینه برای صندلیِ ۱) — برای فرستادنِ حرکت. */
export const nardServerPoint = (p: number, me: number): number => (p < 0 || (me & 1) === 0 ? p : 23 - p);

export const NARD_IVORY = "#f2e6c8", NARD_RED = "#5b1d1d";
export const nardColor = (seat: number, shift: number): string => (((seat + shift) & 1) === 0 ? NARD_IVORY : NARD_RED);

/** پیمودنِ باقی‌مانده تا بیرون — همان BackgammonState.PipCount از چشمِ صندلیِ محلی. */
export function nardPips(m: NardLocal, seat: number): number {
  let pip = m.bar[seat]! * 25;
  for (let i = 0; i < 24; i++) {
    const v = m.pts[i]!;
    if (seat === 0 && v > 0) pip += v * (i + 1);
    if (seat === 1 && v < 0) pip += -v * (24 - i);
  }
  return pip;
}

/**
 * جای خانه‌ی i روی تخته‌ی عمودی (همان BackgammonView): ستونِ چپ از بالا ۱۱…۶ | بار | ۵…۰،
 * ستونِ راست ۱۲…۱۷ | بار | ۱۸…۲۳. خروجی: ستون (چپ؟) و ردیف از بالا (۰..۱۱، بار بعد از ۶ تا).
 */
export function nardSlot(i: number): { left: boolean; row: number } {
  if (i >= 6 && i <= 11) return { left: true, row: 11 - i };
  if (i >= 0 && i <= 5) return { left: true, row: 6 + (5 - i) };
  if (i >= 12 && i <= 17) return { left: false, row: i - 12 };
  return { left: false, row: 6 + (i - 18) };
}

// ─────────────────────────── دومینو ───────────────────────────

export function dominoLocal(st: BoardTableState): DominoLocal {
  const c = common(st);
  const x = (k: number) => st.extra[k] ?? 0;
  const chain: [number, number][] = [];
  for (let k = 0; k + 1 < st.board.length; k += 2) chain.push([st.board[k]!, st.board[k + 1]!]);
  const hand: [number, number][] = [];
  for (let k = 0; k + 1 < st.hand.length; k += 2) hand.push([st.hand[k]!, st.hand[k + 1]!]);
  const counts: number[] = new Array(c.n).fill(0);
  const score: number[] = new Array(c.n).fill(0);
  for (let l = 0; l < c.n; l++) {
    const sv = toServer(l, c.me, c.n);
    counts[l] = st.handCounts[sv] ?? 0;
    score[l] = st.score[sv] ?? 0;
  }
  const mine = c.mine && st.phase === 0;
  return {
    ...c, game: "domino", mine, chain, center: x(0), hand, counts, yard: x(1),
    leftEnd: chain.length ? x(2) : -1, rightEnd: chain.length ? x(3) : -1,
    plays: mine ? st.legal.filter((m) => m.kind === 2).map((m) => ({ index: m.a, side: m.b })) : [],
    canDraw: mine && st.legal.some((m) => m.kind === 3),
    roundOver: st.phase === 1, roundWinner: x(4) >= 0 ? toLocal(x(4), c.me, c.n) : -1,
    roundPoints: x(5), blocked: x(6) === 1, round: st.round, score, shift: c.me,
  };
}

// ─────────────────────────── مارپله ───────────────────────────

export function marpeleLocal(st: BoardTableState): MarpeleLocal {
  const c = common(st);
  const pos: number[] = [];
  for (let l = 0; l < c.n; l++) pos.push(st.board[toServer(l, c.me, c.n)] ?? 0);
  const x = marpeleExtra(st.extra, c.n);
  const powers = Array.from({ length: c.n }, (_, l) => x.powers[toServer(l, c.me, c.n)] ?? { antidote: 0, sword: 0, skip: false, car: 0 });
  const kind2 = st.legal.filter((m) => m.kind === 2);
  return {
    ...c, game: "marpele", pos, die: st.dice[0] ?? 0, dice: [...st.dice], shift: c.me,
    canRoll: c.mine && st.phase === 0,
    choose: c.mine && st.phase === 1 ? kind2.map((m) => m.a) : [],
    bite: c.mine && st.phase === 2
      ? { head: x.bite, antidote: kind2.some((m) => m.a === 1), sword: kind2.some((m) => m.a === 2) } : null,
    pendingHead: x.bite,
    jumps: x.jumps, magics: x.magics, dead: x.dead, powers, theme: x.theme, diceMode: x.diceMode, magic: x.magic,
  };
}

/**
 * رمزِ extra ِ مارپله (marpele.go): [۰، ۲، شمارِ پرش، جفت‌ها…، شمارِ جادو، (خانه، نوع)…،
 * شمارِ مارِ مرده، سرها…، شمارِ صندلی، (پادزهر، شمشیر، ایست)…، تم، تاس، جادو، سرِ منتظر، ماشینِ هر صندلی…].
 * سرورِ پیش از این فقط جفت‌های پرش را می‌فرستاد (marpeleJumps).
 */
export function marpeleExtra(extra: readonly number[], n: number) {
  const empty = { jumps: MARPELE_JUMPS as Readonly<Record<number, number>>, magics: [] as MarpeleMagic[], dead: [] as number[],
    powers: [] as MarpelePower[], theme: 1, diceMode: DICE_ONE, magic: false, bite: 0 };
  if (extra.length < 3 || extra[0] !== 0 || extra[1] !== 2) return { ...empty, jumps: marpeleJumps(extra) };
  let i = 2;
  const take = () => extra[i++] ?? 0;
  const nj = take(), pairs: number[] = [];
  for (let k = 0; k < nj * 2; k++) pairs.push(take());
  const nm = take(), magics: MarpeleMagic[] = [];
  for (let k = 0; k < nm; k++) magics.push({ cell: take(), kind: take() });
  const nd = take(), dead: number[] = [];
  for (let k = 0; k < nd; k++) dead.push(take());
  const np = take(), powers: MarpelePower[] = [];
  for (let k = 0; k < np; k++) powers.push({ antidote: take(), sword: take(), skip: take() === 1, car: 0 });
  const theme = take(), diceMode = take(), magic = take() === 1, bite = take();
  // ماشین آخرِ رمز آمد (سرورِ پیش از آن نمی‌فرستد: take صفر می‌دهد)
  for (const p of powers) p.car = take();
  void n;
  return { jumps: marpeleJumps(pairs), magics, dead, powers, theme, diceMode, magic, bite };
}

/**
 * چیدمان از نما: extra جفت‌جفت [از، به، …] (marpele.Layout.Pairs ِ سرور). خالی یا ناجور =
 * چیدمانِ کلاسیک — سرورِ پیش از تخته‌ی تصادفی چیزی نمی‌فرستاد.
 */
export function marpeleJumps(extra: readonly number[]): Readonly<Record<number, number>> {
  if (extra.length < 2 || extra.length % 2 !== 0) return MARPELE_JUMPS;
  const out: Record<number, number> = {};
  for (let i = 0; i < extra.length; i += 2) {
    const a = extra[i]!, b = extra[i + 1]!;
    if (a < 1 || a > 99 || b < 1 || b > 100 || a === b) return MARPELE_JUMPS;
    out[a] = b;
  }
  return out;
}

/** چیدمانِ کلاسیک — همان SnakeState.Classic و marpele.Classic. */
export const MARPELE_JUMPS: Readonly<Record<number, number>> = {
  1: 38, 4: 14, 9: 31, 21: 42, 28: 84, 36: 44, 51: 67, 71: 91, 80: 100,
  16: 6, 47: 26, 49: 11, 56: 53, 62: 19, 64: 60, 87: 24, 93: 73, 95: 75, 98: 78,
};

/** مرکزِ خانه‌ی n (۱..۱۰۰) در واحدِ خانه (۰..۱۰، y رو به پایین) — شماره‌ی مارپیچی از پایین-چپ. */
export function marpeleXY(n: number): [number, number] {
  const k = Math.min(100, Math.max(1, n));
  const rb = Math.floor((k - 1) / 10), idx = (k - 1) % 10;
  const col = rb % 2 === 0 ? idx : 9 - idx;
  return [col + 0.5, 9 - rb + 0.5];
}

/** رنگِ صندلی‌ها — همان BoardKit.SeatColors: قرمز، آبی، سبز، طلایی. */
export const SEAT_COLORS = ["#d9433a", "#3f7fd4", "#46a24b", "#e0b23a"] as const;
/** آرتِ آدمکِ هم‌رنگِ هر رنگِ SEAT_COLORS — آرت‌ها به ترتیبِ MENCH_COLORS اند (قرمز، سبز، زرد، آبی). */
const SEAT_ART = [0, 3, 1, 2] as const;
/** آدمکِ مارپله (کاربر، ۲۵ اکتبر ۲۰۲۶: «تغییرهای مشترکِ منچ برای مارپله»): شماره‌ی آرتِ هم‌رنگِ صندلیِ p. */
export const seatArt = (p: number, shift: number, n: number): number =>
  SEAT_ART[((((p + shift) % Math.max(1, n)) + Math.max(1, n)) % Math.max(1, n)) % 4]!;
/**
 * گوشه‌ی صندلیِ محلیِ s سرِ میزِ مارپله — همان چیدمانِ گوشه‌های منچ (۰ پایین-چپ، ۱ بالا-چپ، ۲ بالا-راست، ۳
 * پایین-راست): من پایین-چپ، حریف‌ها به نوبت ساعت‌گرد؛ دونفره روبه‌رو (۲).
 */
export const marpeleCorner = (s: number, n: number): number => (s <= 0 ? 0 : n === 2 ? 2 : Math.min(3, s));
/** رنگِ صندلیِ محلیِ p — همان رنگِ صندلیِ سرور روی همه‌ی صفحه‌ها. */
export const seatColor = (p: number, shift: number, n: number): string =>
  SEAT_COLORS[((((p + shift) % Math.max(1, n)) + Math.max(1, n)) % Math.max(1, n)) % 4]!;

/** نمای سرور → مدلِ صفحه، برای هر تخته‌ای. */
export function boardLocal(st: BoardTableState): BoardLocal | null {
  switch (st.game) {
    case "mench": return menchLocal(st);
    case "dooz": return doozLocal(st);
    case "nard": return nardLocal(st);
    case "domino": return dominoLocal(st);
    case "marpele": return marpeleLocal(st);
  }
  return null;
}
